import React, { useMemo } from 'react';
import { transfers } from '../data/transfers';
import { getClubById } from '../data/clubs';

const TransferTable = ({ 
  selectedSellerLeague, 
  selectedBuyerLeague, 
  minAmount, 
  maxAmount, 
  selectedPosition,
  selectedTransferType,
  startDate,
  endDate,
  playerSearch,
  clubSearch,
  excludeOtherLeagues
}) => {
  const filteredTransfers = useMemo(() => {
    return transfers.filter(transfer => {
      if (transfer.season !== "2025/26") return false;
      
      const fromClub = getClubById(transfer.fromClubId);
      const toClub = getClubById(transfer.toClubId);
      
      // League filters
      if (selectedSellerLeague !== 'All' && fromClub?.league !== selectedSellerLeague) return false;
      if (selectedBuyerLeague !== 'All' && toClub?.league !== selectedBuyerLeague) return false;
      
      // Amount filter
      if (transfer.amount < minAmount || transfer.amount > maxAmount) return false;
      
      // Position filter
      if (selectedPosition !== 'All' && transfer.position !== selectedPosition) return false;
      
      // Transfer type filter
      if (selectedTransferType !== 'All') {
        const transferType = transfer.amount === 0 ? 'Free' : 'Paid';
        if (selectedTransferType === 'Loan' && !transfer.player.toLowerCase().includes('loan')) {
          if (transferType !== selectedTransferType) return false;
        } else if (transferType !== selectedTransferType) {
          return false;
        }
      }
      
      // Date range filter
      const transferDate = new Date(transfer.date);
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (transferDate < start || transferDate > end) return false;
      
      // Player search filter
      if (playerSearch && !transfer.player.toLowerCase().includes(playerSearch.toLowerCase())) return false;
      
      // Club search filter
      if (clubSearch) {
        const fromClubName = fromClub?.name.toLowerCase() || '';
        const toClubName = toClub?.name.toLowerCase() || '';
        const searchTerm = clubSearch.toLowerCase();
        if (!fromClubName.includes(searchTerm) && !toClubName.includes(searchTerm)) return false;
      }
      
      // Exclude other leagues filter
      if (excludeOtherLeagues) {
        const majorLeagues = ['Premier League', 'La Liga', 'Bundesliga', 'Serie A', 'Ligue 1'];
        if (!majorLeagues.includes(fromClub?.league) && !majorLeagues.includes(toClub?.league)) {
          return false;
        }
      }
      
      return true;
    });
  }, [
    selectedSellerLeague, 
    selectedBuyerLeague, 
    minAmount, 
    maxAmount, 
    selectedPosition,
    selectedTransferType,
    startDate,
    endDate,
    playerSearch,
    clubSearch,
    excludeOtherLeagues
  ]);

  const formatAmount = (amount) => {
    if (amount >= 1000000) {
      return `€${(amount / 1000000).toFixed(1)}M`;
    }
    return `€${(amount / 1000).toFixed(0)}K`;
  };

  return (
    <div className="transfer-table-container">
      <div className="transfer-table-header">
        <h3>Transfer Table ({filteredTransfers.length} transfers)</h3>
      </div>
      <div className="transfer-table-content">
        <table className="transfer-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Amount</th>
              <th>Date</th>
              <th>From Club</th>
              <th>To Club</th>
              <th>Position</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransfers.map(transfer => {
              const fromClub = getClubById(transfer.fromClubId);
              const toClub = getClubById(transfer.toClubId);
              return (
                <tr key={transfer.id}>
                  <td>
                    <div className="player-cell">
                      <img 
                        src={transfer.playerImage || '/images/player-placeholder.svg'} 
                        alt={transfer.player}
                        className="player-image"
                        onError={(e) => {
                          e.target.src = '/images/player-placeholder.svg';
                        }}
                      />
                      <span>{transfer.player}</span>
                    </div>
                  </td>
                  <td className="amount-cell">{formatAmount(transfer.amount)}</td>
                  <td>{transfer.date}</td>
                  <td>
                    <div className="club-cell">
                      <img 
                        src={fromClub?.crest || '/images/crest-placeholder.svg'} 
                        alt={fromClub?.name}
                        className="club-crest"
                        onError={(e) => {
                          e.target.src = '/images/crest-placeholder.svg';
                        }}
                      />
                      <span>{fromClub?.name}</span>
                    </div>
                  </td>
                  <td>
                    <div className="club-cell">
                      <img 
                        src={toClub?.crest || '/images/crest-placeholder.svg'} 
                        alt={toClub?.name}
                        className="club-crest"
                        onError={(e) => {
                          e.target.src = '/images/crest-placeholder.svg';
                        }}
                      />
                      <span>{toClub?.name}</span>
                    </div>
                  </td>
                  <td>{transfer.position}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TransferTable;

